// This file is used for parsing listing page URL string and converting it to attributes in JS object.
// It also contains function for the reversed operation, thus converting attributes JS object to
// listing page URL
// You can read more info about listing page URLs in README.md file in this folder

// Regex expressions used throughout this file
const singleIdRegEx = String.raw`(\d+|xx)`;
const brandIdsRegEx = String.raw`(\d+(?:_\d+)*|xx)`;
const roundedScoreRegEx = String.raw`(rounded_score-(\d+)(%7C\d+)*)`;
const deliveryRegex = String.raw`(-?delivery-(\w+)(%7C\w+)*)`;
const dynamicFiltersRegex = String.raw`(-?ba[\w]+-bv[\w]+(?:%7Cbv[\w]+)*)+`;
const filtersRegex = String.raw`((${roundedScoreRegEx}?${deliveryRegex}?(${dynamicFiltersRegex})?)|xx)`;
const pagingAndSortingRegex = String.raw`(\/(\d+)_(\d+)\/(\w+(?:-\w+)?))`;
const priceRangeRegEx = String.raw`(?:(\w+)-(\w+)?)|(\w|-)+`;

const tagsRegex = String.raw`-(.+)-`;

// the delimiter between skus and fupids should be only %7c which is escaped '|' char but due to errors
// in escaping throughout the platform the delimiter could be also %257c
const skusRegex = String.raw`^([0-9]{6})(%(25)?7c([0-9]{6}))*$`;
const fupidsRegex = String.raw`^([0-9]{8})(%(25)?7c([0-9]{8}))*$`;


// Parses listing page URL string and converting it to attributes in JS object which identifies uniquely each listing page
// Only the URL suffix is used for generating the object
// The JS object is used on many places in code
// It's used e. g. for generating request URL to Search API
// JS object is stored in redux store for each page in listingPage.pages[n].pageAttributes
//
// This function is also used on server where detailed logging is required so it's written with rich logging info as web service
// It always returns status object {ok: true || false , ...} and it should never finish with error
//
/*
Example of JS object for this url suffix of listing page
/301_3002_30002_43_49_rounded_score-10-delivery-hd-ba00004464-bv00308620/xx-criteria.html:
{
  page: '1', // number of this page
  m_results_per_page: '20', // products per page settings
  sort: 'relevance-desc' // sorting of this page
  filterAttributes: { // filters selected for this page
    category_id: [
      '301' // category of this page
    ],
      market_id: [
      '3002' market of this page
    ],
      segment_id: [
      '30002' segment of this page
    ],
      brand_id: [
      '43', brand filters selected
      '49'
    ],
      rounded_score: [
      '10' customer rating filter selected
    ],
      delivery: [
      'hd' delivery filter selected
    ],
      ba00004464: [
      'bv00308620' / some dynamic filter selected
    ]
  }
}
 */
export const parseListingPageAttributesFromUrl = (listingPageUrl) => {

  if (!listingPageUrl.split('?')[0].endsWith('criteria.html')) {
    return {
      ok: false,
      customErrorMessage: `The listing page URL ${listingPageUrl} is not in valid format because the URL doesn't finishes ` +
      'with "criteria.html" suffix.',
      errorCode: 404
    };
  }

  const isSearchListingPage = listingPageUrl.includes('/search-keywords/');

  const searchPrefix = isSearchListingPage ? 'search-keywords/' : '';
  const decodedUrl = listingPageUrl;

  if (isSearchListingPage) {
    const searchListingPageRegEx = new RegExp(String.raw`^.*/search-keywords/${singleIdRegEx}_${singleIdRegEx}_${singleIdRegEx}` +
      String.raw`_${brandIdsRegEx}_${filtersRegex}/.+${pagingAndSortingRegex}?/${priceRangeRegEx}-criteria\.html$`, 'ig');

    if (!searchListingPageRegEx.test(decodedUrl)) {
      return {
        ok: false,
        customErrorMessage: `The search page URL ${listingPageUrl} is not in valid format because it hasn't passed RegEx test in 
          src file /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js`,
        errorCode: 404
      };
    }
  } else {
    const segmentListingPageRegEx = new RegExp(String.raw`^.*/${singleIdRegEx}_${singleIdRegEx}_${singleIdRegEx}` +
      String.raw`_${brandIdsRegEx}_${filtersRegex}${pagingAndSortingRegex}?/${priceRangeRegEx}-criteria\.html$`, 'ig');


    if (!segmentListingPageRegEx.test(decodedUrl)) {
      return {
        ok: false,
        customErrorMessage: `The listing page URL ${listingPageUrl} is not in valid format because it hasn't passed RegEx test in 
          src file /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js`,
        errorCode: 404
      };
    }
  }

  const results = { filterAttributes: {} };

  const idsAndFiltersRegexMatch =
    new RegExp(String.raw`.*/${searchPrefix}${singleIdRegEx}_${singleIdRegEx}_${singleIdRegEx}_${brandIdsRegEx}_.+/`,
      'i').exec(decodedUrl);

  const roundedScoreRegExMatch =
    new RegExp(roundedScoreRegEx, 'i').exec(decodedUrl);

  const deliveryRegExMatch =
    new RegExp(deliveryRegex, 'i').exec(decodedUrl);

  const dynamicFiltersRegexMatch =
    new RegExp(dynamicFiltersRegex, 'i').exec(decodedUrl);

  const searchQueryRegExMatch =
    new RegExp(String.raw`.*/${searchPrefix}[^/]+/([^/]+)/.+`, 'i').exec(decodedUrl);

  const pagingAndSortingRegexMatch =
    new RegExp(String.raw`.*/${searchPrefix}.+${pagingAndSortingRegex}/.+`, 'i').exec(decodedUrl);

  const priceRangeRegExMatch =
    new RegExp(String.raw`.*/${priceRangeRegEx}-criteria\.html`).exec(decodedUrl);

  if (Array.isArray(idsAndFiltersRegexMatch) && idsAndFiltersRegexMatch.length >= 5) {
    if (idsAndFiltersRegexMatch[1] !== 'xx') {
      results.filterAttributes.category_id = [idsAndFiltersRegexMatch[1]];
    }
    if (idsAndFiltersRegexMatch[2] !== 'xx') {
      results.filterAttributes.market_id = [idsAndFiltersRegexMatch[2]];
    }
    if (idsAndFiltersRegexMatch[3] !== 'xx') {
      results.filterAttributes.segment_id = [idsAndFiltersRegexMatch[3]];
    }

    if (idsAndFiltersRegexMatch[4] !== 'xx') {
      results.filterAttributes.brand_id = idsAndFiltersRegexMatch[4].split('_');
    }
  } else {
    return {
      ok: false,
      customErrorMessage: `The listing page URL ${listingPageUrl} has incorrect format of category_id, market_id, segment_id, 
        brand ids or filters.<br>Check parsing of URL in function 'parseListingPageAttributesFromUrl()' in 
        /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js file`,
      errorCode: 404
    };
  }


  if (isSearchListingPage && searchQueryRegExMatch) {
    const tagsRegexMatch = (new RegExp(tagsRegex)).exec(searchQueryRegExMatch[1]);

    if (tagsRegexMatch) {
      results.filterAttributes.tag = tagsRegexMatch[1];
    } else if ((new RegExp(skusRegex)).test(searchQueryRegExMatch[1])) {
      results.filterAttributes.externalref = searchQueryRegExMatch[1];
    } else if ((new RegExp(fupidsRegex)).test(searchQueryRegExMatch[1])) {
      results.filterAttributes.id = searchQueryRegExMatch[1];
    } else {
      results.query = decodeURIComponent(searchQueryRegExMatch[1]);
    }
  }

  if (roundedScoreRegExMatch) {
    if (Array.isArray(roundedScoreRegExMatch) && roundedScoreRegExMatch.length >= 1) {
      const matchWithoutPrefix = roundedScoreRegExMatch[1].replace('rounded_score-', '');
      results.filterAttributes.rounded_score = matchWithoutPrefix.split('%7C'); // %7C is escaped '|' char
    } else {
      return {
        ok: false,
        customErrorMessage: `The listing page URL ${listingPageUrl} has incorrect format of rounded_score filter.  
          (Reevoo review score displayed by stars).<br>Check parsing of URL in function 'parseListingPageAttributesFromUrl()' in 
          /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js file`,
        errorCode: 404
      };
    }
  }

  if (deliveryRegExMatch) {
    if (Array.isArray(deliveryRegExMatch) && deliveryRegExMatch.length >= 1) {
      const matchWithoutFirstDashAndPrefix =
        deliveryRegExMatch[0].charAt(0) === '-'
          ? deliveryRegExMatch[0].substr(1).replace('delivery-', '')
          : deliveryRegExMatch[0].replace('delivery-', '');
      results.filterAttributes.delivery = matchWithoutFirstDashAndPrefix.split('%7C'); // %7C is escaped '|' char
    } else {
      return {
        ok: false,
        customErrorMessage: `The listing page URL ${listingPageUrl} has incorrect format of deliveries filter.  
          <br>Check parsing of URL in function 'parseListingPageAttributesFromUrl()' in 
          /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js file`,
        errorCode: 404
      };
    }

  }

  if (dynamicFiltersRegexMatch) {
    if (Array.isArray(dynamicFiltersRegexMatch) && dynamicFiltersRegexMatch.length >= 1) {
      const matchWithoutFirstDash =
        dynamicFiltersRegexMatch[0].charAt(0) === '-' ? dynamicFiltersRegexMatch[0].substr(1) : dynamicFiltersRegexMatch[0];
      const attributesAndValues = matchWithoutFirstDash.split('-');

      for (let index = 0; index < attributesAndValues.length; index += 2) {
        const attrId = attributesAndValues[index];
        results.filterAttributes[attrId] = attributesAndValues[index + 1].split('%7C'); // %7C is escaped '|' char;
      }
    } else {
      return {
        ok: false,
        customErrorMessage: `The listing page URL ${listingPageUrl} has incorrect format of dynamic filters 
          (e.g. ba00013254-bv00312212 etc.).<br>Check parsing of URL in function 'parseListingPageAttributesFromUrl()' in 
          /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js file`,
        errorCode: 404

      };
    }

  }

  if (pagingAndSortingRegexMatch) {

    if (Array.isArray(pagingAndSortingRegexMatch) && pagingAndSortingRegexMatch.length >= 5) {
      results.page = pagingAndSortingRegexMatch[2];
      results.m_results_per_page = pagingAndSortingRegexMatch[3];
      results.sort = pagingAndSortingRegexMatch[4];
    } else {
      return {
        ok: false,
        customErrorMessage: `The listing page URL ${listingPageUrl} has incorrect format of paging or sorting. 
          <br>Check parsing of URL in function 'parseListingPageAttributesFromUrl()' in 
          /fo-app/src/modules/ListingPage/listingPageUrlParser/listingPageUrlParser.js file`,
        errorCode: 404
      };
    }

  } else {
    // default paging and sorting
    results.page = '1';
    results.m_results_per_page = '20';
    results.sort = 'relevance-desc';
  }


  // parsing price ranges
  let priceMinString;
  let priceMaxString;

  if (typeof priceRangeRegExMatch[1] !== 'undefined' && Number.isInteger(Number(priceRangeRegExMatch[1]))) {
    priceMinString = priceRangeRegExMatch[1];

  }
  if (typeof priceRangeRegExMatch[2] !== 'undefined' && Number.isInteger(Number(priceRangeRegExMatch[2]))) {
    priceMaxString = priceRangeRegExMatch[2];
  }

  if ((priceMinString && priceMaxString) &&
    Number(priceMinString) <= Number(priceMaxString) &&
    Number(priceMaxString) !== 0 // this subcondition is here to take care of case when URL suffix is 0-0-criteria.html which should behave like there is no price filter suffix: xx-criteria
  ) {
    results.price_min = priceMinString;
    results.price_max = priceMaxString;
  } else if (priceMinString &&
    Number(priceMinString) !== 0 && // this subcondition is here to take care of case when URL suffix is 0-criteria.html or 0-xx-criteria.html. Both suffixes should behave like there is no price filter suffix: xx-criteria
    !priceMaxString
  ) {
    results.price_min = priceMinString;
  } else if (priceMaxString && priceRangeRegExMatch[1] === 'xx') {
    results.price_min = '0'; // THERE HAS TO BE A STRING DUE TO URL NORMALIZER
    results.price_max = priceMaxString;
  }

  return {
    ok: true,
    listingPageAttributes: results
  };
};

// Reverse function to parseListingPageAttributesFromUrl()
export const createListingPageUrlFromAttributes = (attributes = {}) => {
  let url = '/';

  if (attributes.query || attributes?.filterAttributes?.tag || attributes?.filterAttributes?.externalref ||
    attributes?.filterAttributes?.id) {
    url += 'search-keywords/';
  }

  if (attributes.filterAttributes && attributes.filterAttributes.category_id) {
    url += `${attributes.filterAttributes.category_id[0]}_`;
  } else {
    url += 'xx_';
  }

  if (attributes.filterAttributes && attributes.filterAttributes.market_id) {
    url += `${attributes.filterAttributes.market_id[0]}_`;
  } else {
    url += 'xx_';
  }

  if (attributes.filterAttributes && attributes.filterAttributes.segment_id) {
    url += `${attributes.filterAttributes.segment_id[0]}_`;
  } else {
    url += 'xx_';
  }

  if (attributes.filterAttributes && attributes.filterAttributes.brand_id) {
    url += attributes.filterAttributes.brand_id.join('_');
    url += '_';
  } else {
    url += 'xx_';
  }

  let noFiltersInUrl = true;

  if (attributes.filterAttributes && attributes.filterAttributes.rounded_score) {
    url += `rounded_score-${attributes.filterAttributes.rounded_score.join('%7C')}`; // %7C is escaped '|' char
    noFiltersInUrl = false;
  }

  if (attributes.filterAttributes && attributes.filterAttributes.delivery) {
    if (url.charAt(url.length - 1) !== '_') {
      url += '-';
    }
    url += `delivery-${attributes.filterAttributes.delivery.join('%7C')}`; // %7C is escaped '|' char
    noFiltersInUrl = false;
  }

  if (typeof attributes.filterAttributes === 'object') {
    Object.keys(attributes.filterAttributes).forEach((attrName) => {

      if (attrName.startsWith('ba') && typeof attributes.filterAttributes[attrName] !== 'undefined') {
        if (url.charAt(url.length - 1) !== '_') {
          url += '-';
        }
        url += `${attrName}-${attributes.filterAttributes[attrName].join('%7C')}`; // %7C is escaped '|' char
        noFiltersInUrl = false;
      }
    });
  }


  if (noFiltersInUrl) {
    url += 'xx';
  }

  if (attributes.query) {
    url += `/${encodeURIComponent(attributes.query)}`;
  }

  if (attributes?.filterAttributes?.tag) {
    url += `/-${attributes.filterAttributes.tag}-`;
  }

  if (attributes?.filterAttributes?.externalref) {
    url += `/${attributes.filterAttributes.externalref}`;
  }

  if (attributes?.filterAttributes?.id) {
    url += `/${attributes.filterAttributes.id}`;
  }

  // setting default values for paging, results per page and sorting, if not defined
  const pageNumber = attributes.page ?? '1';
  const resultsPerPage = attributes.m_results_per_page ?? '20';
  const sorting = attributes.sort ?? 'relevance-desc';

  if (pageNumber !== '1' || resultsPerPage !== '20' || sorting !== 'relevance-desc') {
    // we add paging, results per page and sorting to URL if any value from them doesn't have default value
    url += `/${pageNumber}_${resultsPerPage}/${sorting}`;
  }

  if (attributes.price_min) {
    url += `/${attributes.price_min}${attributes.price_max ? `-${attributes.price_max}` : ''}-criteria.html`;
  } else {
    url += '/xx-criteria.html';
  }

  return url;
};
